import { useState } from "react";
import useAuth from "../../hooks/useAuth";
import { Dropdown } from "flowbite-react";
import {
    Contact,
    LayoutDashboard,
    FileText,
    Heart,
    User,
    ShieldCheck,
    LogOut,
    Car,
    Wallet,
    Menu as MenuIcon,
    X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getFilesUrl } from "../../services/getFileUrl";
import { useSelectCurrency } from "../../hooks/useSelectCurrency";
import { currencies } from "../../datas/filters";

const headerTabs = [
    { href: "/inventory", title: "Inventory" },
    { href: "/about-us", title: "About Us" },
    { href: "/contact", title: "Contact" },
];

// `customerOnly` items are hidden from admins (admins don't buy/trade).
const menuItems = [
    { to: "/account/dashboard", label: "Dashboard", icon: LayoutDashboard, customerOnly: true },
    { to: "/account/activate", label: "Activate Trading", icon: Wallet, customerOnly: true },
    { to: "/account/request-quote", label: "Request a Car", icon: Car, customerOnly: true },
    { to: "/account/quotes", label: "My Requests", icon: FileText, customerOnly: true },
    { to: "/account/saved", label: "Saved Cars", icon: Heart, customerOnly: true },
    { to: "/account/profile", label: "Profile", icon: User, customerOnly: false },
];

function Header() {
    const navigate = useNavigate();
    const { authState, logout } = useAuth();
    const { selectedCurrency, updateCurrency } = useSelectCurrency();
    const user = authState.user;
    const [mobileOpen, setMobileOpen] = useState(false);

    const onCurrencyChange = (value: string) => {
        const c = currencies.find((x) => x.currency === value);
        if (c) updateCurrency(c.currency, c.locales);
    };

    const CurrencySelect = ({ className = "" }: { className?: string }) => (
        <select
            aria-label='Display currency'
            value={selectedCurrency.currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className={"text-sm font-semibold text-gray-600 bg-transparent border border-gray-200 rounded-lg px-2 py-1.5 cursor-pointer focus:ring-0 " + className}>
            {currencies.map((c) => (
                <option key={c.currency} value={c.currency}>
                    {c.currency}
                </option>
            ))}
        </select>
    );

    const go = (to: string) => {
        setMobileOpen(false);
        navigate(to);
    };

    const handleLogout = () => {
        setMobileOpen(false);
        logout();
        navigate("/");
    };

    const avatarUrl =
        user && user.avatar ? getFilesUrl(user as any, user.avatar, { thumb: "80x80" }) : "";
    const initial = (user?.name?.[0] || user?.email?.[0] || "?").toUpperCase();
    const visibleMenuItems = menuItems.filter((m) => !(m.customerOnly && authState.isAdmin));

    const AvatarCircle = () => (
        <div className='w-9 h-9 rounded-full bg-purple-100 text-purple-700 overflow-hidden flex items-center justify-center font-bold'>
            {avatarUrl ? (
                <img src={avatarUrl} alt='' className='w-full h-full object-cover' />
            ) : (
                initial
            )}
        </div>
    );

    return (
        <header className='relative bg-white border-b border-gray-100 z-50'>
            <div className='flex items-center justify-between py-4 px-4 md:px-8 lg:px-32'>
                {/* Left: hamburger (mobile) + logo */}
                <div className='flex items-center gap-3'>
                    <button
                        type='button'
                        className='md:hidden text-gray-700'
                        aria-label='Toggle menu'
                        onClick={() => setMobileOpen((o) => !o)}>
                        {mobileOpen ? <X /> : <MenuIcon />}
                    </button>
                    <Link to='/'>
                        <img src='/logo192.png' alt='Atech Japan' className='w-10' />
                    </Link>
                </div>

                {/* Center: desktop tabs */}
                <nav className='hidden md:flex items-center font-semibold md:gap-4 xl:gap-10'>
                    {headerTabs.map((tab) => (
                        <Link to={tab.href} key={tab.title} className='relative group'>
                            {tab.title}
                            <span className='absolute inset-x-0 bottom-[-5px] h-1 bg-black transform scale-x-0 origin-left transition-transform duration-300 ease-in-out group-hover:scale-x-100'></span>
                        </Link>
                    ))}
                </nav>

                {/* Right: desktop currency + auth */}
                <div className='hidden md:flex items-center gap-3'>
                    <CurrencySelect />
                    {authState.isAuthenticated ? (
                        <Dropdown
                            arrowIcon={false}
                            inline
                            label=''
                            renderTrigger={() => (
                                <span className='cursor-pointer'>
                                    <AvatarCircle />
                                </span>
                            )}>
                            <Dropdown.Header>
                                <span className='block text-sm font-semibold truncate'>
                                    {user?.name || "My account"}
                                </span>
                                <span className='block text-xs text-gray-500 truncate'>
                                    {user?.email}
                                </span>
                            </Dropdown.Header>
                            {visibleMenuItems.map(({ to, label, icon: Icon }) => (
                                <Dropdown.Item key={to} icon={Icon} onClick={() => navigate(to)}>
                                    {label}
                                </Dropdown.Item>
                            ))}
                            {authState.isAdmin && (
                                <Dropdown.Item icon={ShieldCheck} onClick={() => navigate("/account/admin")}>
                                    Admin
                                </Dropdown.Item>
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item icon={LogOut} onClick={handleLogout}>
                                Logout
                            </Dropdown.Item>
                        </Dropdown>
                    ) : (
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={() => navigate("/account/sign-up")}
                                className='bg-gray-500 hover:bg-gray-600 text-white py-1.5 px-4 rounded-lg flex items-center gap-2 border-none'>
                                <Contact strokeWidth={1.5} size={18} /> Sign Up
                            </button>
                            <button
                                onClick={() => navigate("/account/sign-in")}
                                className='bg-purple-600 hover:bg-purple-700 text-white py-1.5 px-4 rounded-lg'>
                                Sign In
                            </button>
                        </div>
                    )}
                </div>

                {/* Right: mobile — avatar (tap opens the single mobile menu) or auth buttons */}
                <div className='md:hidden'>
                    {authState.isAuthenticated ? (
                        <button type='button' onClick={() => setMobileOpen((o) => !o)} aria-label='Account menu'>
                            <AvatarCircle />
                        </button>
                    ) : (
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={() => navigate("/account/sign-up")}
                                className='bg-gray-500 hover:bg-gray-600 text-white py-1.5 px-3 rounded-lg text-sm border-none'>
                                Sign Up
                            </button>
                            <button
                                onClick={() => navigate("/account/sign-in")}
                                className='bg-purple-600 hover:bg-purple-700 text-white py-1.5 px-3 rounded-lg text-sm'>
                                Sign In
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile menu: single overlay dropdown */}
            {mobileOpen && (
                <>
                    {/* click-away backdrop */}
                    <div
                        className='md:hidden fixed inset-0 top-[64px] z-40 bg-black/20'
                        onClick={() => setMobileOpen(false)}
                    />
                    <div className='md:hidden absolute left-0 right-0 top-full z-50 bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto'>
                        <nav className='flex flex-col py-2'>
                            {headerTabs.map((tab) => (
                                <button
                                    key={tab.title}
                                    onClick={() => go(tab.href)}
                                    className='text-left px-6 py-3 font-medium text-gray-800 hover:bg-gray-50'>
                                    {tab.title}
                                </button>
                            ))}

                            <div className='px-6 py-3 flex items-center gap-3'>
                                <span className='text-sm text-gray-500'>Currency</span>
                                <CurrencySelect />
                            </div>

                            <div className='border-t border-gray-100 my-1' />

                            {authState.isAuthenticated ? (
                                <>
                                    <div className='px-6 py-2'>
                                        <div className='text-sm font-semibold text-slate-950'>
                                            {user?.name || "My account"}
                                        </div>
                                        <div className='text-xs text-gray-500 truncate'>{user?.email}</div>
                                    </div>
                                    {visibleMenuItems.map(({ to, label, icon: Icon }) => (
                                        <button
                                            key={to}
                                            onClick={() => go(to)}
                                            className='flex items-center gap-3 text-left px-6 py-3 text-gray-800 hover:bg-gray-50'>
                                            <Icon size={18} /> {label}
                                        </button>
                                    ))}
                                    {authState.isAdmin && (
                                        <button
                                            onClick={() => go("/account/admin")}
                                            className='flex items-center gap-3 text-left px-6 py-3 text-gray-800 hover:bg-gray-50'>
                                            <ShieldCheck size={18} /> Admin
                                        </button>
                                    )}
                                    <button
                                        onClick={handleLogout}
                                        className='flex items-center gap-3 text-left px-6 py-3 text-rose-600 hover:bg-gray-50'>
                                        <LogOut size={18} /> Logout
                                    </button>
                                </>
                            ) : null}
                        </nav>
                    </div>
                </>
            )}
        </header>
    );
}

export default Header;
